/* eslint-disable react/display-name */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Styles from './Select.css';
import TextField from '../TextField/TextField';
import clsx from 'clsx';
import ClickAwayListener from 'react-click-away-listener';
import { useLockPageScroll } from '../../../../utils/useLayout';
import { useStoreActions } from '../../model/actions/use-store-actions';
import { devLog } from '../../../../utils/browser';
import debounce from 'lodash/debounce';
import { maskTextAndNumbers } from '../../shared/utils/masks';

const formatData = (d = []) => {
  const f = d.map((item) => ({
    label: item.name,
    value: `${item.id}`,
  }));

  return f;
};

/**
 *
 * @type {import('../../../../../types/SimuladorEnergiaSolar').SelectComponent}
 */
const SelectStore = React.forwardRef(
  (
    {
      allowClear,
      lockPageScroll,
      touched = false,
      singleLine = false,
      // eslint-disable-next-line no-unused-vars
      options = [],
      // eslint-disable-next-line no-unused-vars
      handleValueChange,
      // eslint-disable-next-line no-unused-vars
      onChange,
      // eslint-disable-next-line no-unused-vars
      value,
      error,
      helperText,
      ...props
    },
    ref,
  ) => {
    const [search, setSearch] = useState('');
    const [selectedOption, setSelectedOption] = useState(null);
    const [showOptions, setShowOptions] = useState(false);
    const [data, setData] = useState(formatData([]));
    const { loadingStores, findStoresByText } = useStoreActions();

    useLockPageScroll(lockPageScroll && showOptions);

    const displayValue = useMemo(() => {
      if (showOptions && !search) return '';
      return search || selectedOption?.label || '';
    }, [search, showOptions, selectedOption?.label]);

    const handleSearchStoreByText = useCallback(
      async (q) => {
        if (loadingStores) return;

        devLog(`query: ${q}`);

        const s = `${q || ''}`.trim();
        if (!s || s.length < 3) {
          devLog('search is empty or less than 3 characters, skipping search');
          return;
        }

        const res = await findStoresByText(s);
        setData(formatData(res));
      },
      [findStoresByText, loadingStores],
    );

    const delayedHandleSearchStore = useMemo(
      () => debounce(handleSearchStoreByText, 750),
      [handleSearchStoreByText],
    );

    const internalError = useMemo(() => {
      const invalidSearch = `${search}`.trim().length < 3;
      if (touched && invalidSearch && data.length === 0) {
        return {
          error: true,
          helperText: 'Digite pelo menos 3 caracteres para buscar uma loja',
        };
      }

      if (showOptions && !selectedOption) {
        return {
          error: false,
          helperText: '',
        };
      }

      return {
        error,
        helperText,
      };
    }, [data.length, error, helperText, search, selectedOption, showOptions, touched]);

    const showClear = allowClear && selectedOption?.value;

    const updateValue = (option) => {
      setSearch('');
      setShowOptions(false);
      setSelectedOption(option);
      handleValueChange(option?.value || '');
    };

    useEffect(() => {
      if (loadingStores) return;
      delayedHandleSearchStore(search);
    }, [delayedHandleSearchStore, loadingStores, search]);

    return (
      <>
        <ClickAwayListener
          onClickAway={() => {
            setShowOptions(false);
            setSearch('');
          }}
        >
          <div
            className={clsx({
              'select-field': true,
              'show-clear': showClear,
              'single-line': singleLine,
            })}
          >
            <TextField
              {...props}
              {...internalError}
              ref={ref}
              autoComplete="off"
              aria-autocomplete="none"
              type="text"
              value={displayValue}
              onChange={(ev) => {
                ev.preventDefault();
                setSearch(maskTextAndNumbers(ev.target.value));
              }}
              onFocus={(ev) => {
                if (props.onFocus) {
                  props.onFocus(ev);
                }

                setTimeout(() => {
                  setShowOptions(true);
                }, 75);
              }}
              endAdornment={
                <>
                  {showClear && (
                    <img
                      src="assets/simulador-energia-solar/jornada/ic-clear.svg"
                      alt="limpar"
                      role="button"
                      onClick={(ev) => {
                        ev.preventDefault();
                        updateValue(null);
                        setData([]);
                      }}
                    />
                  )}

                  {!showClear && (
                    <img src="assets/simulador-energia-solar/jornada/ic-dropdown.svg" alt="" />
                  )}
                </>
              }
              data-selected={selectedOption?.value}
            />

            <div
              className={clsx({
                'select-element': true,
                active: showOptions && !internalError?.error,
              })}
            >
              {loadingStores && (
                <div
                  className={clsx({
                    'option-element': true,
                    disabled: true,
                    selected: false,
                  })}
                  key="loading"
                  value=""
                  onClick={(ev) => {
                    ev.preventDefault();
                  }}
                >
                  <p>Procurando lojas...</p>
                </div>
              )}

              {!loadingStores && data.length === 0 && (
                <div
                  className={clsx({
                    'option-element': true,
                    disabled: true,
                    selected: false,
                  })}
                  key="loading"
                  value=""
                  onClick={(ev) => {
                    ev.preventDefault();
                  }}
                >
                  <p>Nenhuma loja encontrada</p>
                </div>
              )}

              {!loadingStores &&
                data.length > 0 &&
                data.map((option) => (
                  <div
                    className={clsx({
                      'option-element': true,
                      disabled: option.disabled,
                      selected: option.value === selectedOption?.value,
                    })}
                    key={option.value}
                    value={option.value}
                    onClick={(ev) => {
                      ev.preventDefault();
                      if (option.disabled) return;

                      updateValue(option);
                    }}
                  >
                    <p>{option.label}</p>
                  </div>
                ))}
            </div>
          </div>
        </ClickAwayListener>
        <style jsx>{Styles}</style>
      </>
    );
  },
);

export default SelectStore;
